import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usersAPI, subscriptionsAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Users, Search, Plus, Pencil, Mail, Building2,
  Shield, Eye, EyeOff, X, Trash2, Camera,
} from 'lucide-react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/* ============================================================
   USER FORM MODAL
   ============================================================ */
function UserFormModal({ user, companies, onClose, onSave }) {
  const [form, setForm] = useState(user || {
    username: '', email: '', full_name: '', password: '',
    role_name: 'User', company_id: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (user?.id) {
        await usersAPI.updateAll(user.id, form);
        toast.success('User updated');
      } else {
        await usersAPI.createAll(form);
        toast.success('User created');
      }
      onSave();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{user?.id ? 'Edit User' : 'New User'}</h3>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="sa-form-group">
                <label className="sa-form-label">Full Name *</label>
                <input className="sa-input" required value={form.full_name || ''} onChange={e => setForm({...form, full_name: e.target.value})} />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Username *</label>
                <input className="sa-input" required value={form.username} onChange={e => setForm({...form, username: e.target.value})} />
              </div>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Email *</label>
              <input className="sa-input" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">{user?.id ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <div style={{ position: 'relative' }}>
                <input className="sa-input" type={showPw ? 'text' : 'password'} required={!user?.id} value={form.password || ''} onChange={e => setForm({...form, password: e.target.value})} style={{ paddingRight: 36 }} />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="sa-form-group">
                <label className="sa-form-label">Role</label>
                <select className="sa-input sa-select" value={form.role_name || 'User'} onChange={e => setForm({...form, role_name: e.target.value})}>
                  <option value="Administrator">Administrator</option>
                  <option value="User">User</option>
                  <option value="Accountant">Accountant</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Company</label>
                <select className="sa-input sa-select" value={form.company_id || ''} onChange={e => setForm({...form, company_id: e.target.value || null})}>
                  <option value="">Platform (no company)</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   USERS PAGE
   ============================================================ */
export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(null);
  const avatarInputRef = useRef(null);
  const avatarTargetRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [u, c] = await Promise.all([
        usersAPI.listAll().catch(() => usersAPI.list()),
        subscriptionsAPI.companies({}).catch(() => []),
      ]);
      setUsers(u);
      setCompanies(c);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Normalize user objects (backend returns first_name/last_name, frontend uses full_name)
  const normalizedUsers = users.map(u => ({
    ...u,
    full_name: u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
  }));

  const filtered = normalizedUsers.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q);
  });

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user \"${user.full_name || user.username}\"?\n\nThis cannot be undone!`)) return;
    try {
      await usersAPI.deleteAll(user.id);
      toast.success('User deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  // Superadmin: upload/replace a profile picture for any user
  const openAvatarPicker = (user) => {
    avatarTargetRef.current = user;
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
      avatarInputRef.current.click();
    }
  };
  const handleAvatarSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    const target = avatarTargetRef.current;
    if (!file || !target) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error('Image must be under 4 MB'); return; }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await usersAPI.uploadAvatar(target.id, dataUrl);
      toast.success(`Profile picture updated for ${target.full_name || target.username}`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const getCompanyName = (user) => {
    if (!user.company_id) return 'Platform';
    // Prefer company_name from joined query
    if (user.company_name) return user.company_name;
    const co = companies.find(c => c.id === user.company_id);
    return co?.name || `Company #${user.company_id}`;
  };

  return (
    <div>
      {/* Hidden avatar file input */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleAvatarSelected}
      />
      {/* Toolbar */}
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <div className="sa-search-box" style={{ width: 260 }}>
            <Search size={14} />
            <input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className="sa-toolbar-title">{filtered.length} users</span>
        </div>
        <button className="sa-btn sa-btn-primary" onClick={() => setShowForm({})}>
          <Plus size={14} /> New User
        </button>
      </div>

      {/* Table */}
      <div className="sa-card">
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Company</th>
                <th>Status</th>
                <th>Joined</th>
                <th className="sa-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="sa-empty-row"><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : filtered.length === 0 ? (
                <tr className="sa-empty-row"><td colSpan={7}>No users found</td></tr>
              ) : filtered.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {u.avatar_path ? (
                        <img src={fileUrl(u.avatar_path)} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: u.role_name === 'Administrator' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'linear-gradient(135deg, #94a3b8, #64748b)',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(u.full_name || u.username || '?')[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="sa-company-cell">
                        <strong>{u.full_name || u.username}</strong>
                        <span>@{u.username}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{u.email}</td>
                  <td>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 4,
                      fontSize: 11, fontWeight: 700,
                      background: u.role_name === 'Administrator' ? '#dbeafe' : '#f1f5f9',
                      color: u.role_name === 'Administrator' ? '#1e40af' : '#475569',
                    }}>
                      <Shield size={10} />
                      {u.role_name || 'User'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{getCompanyName(u)}</td>
                  <td>
                    <span className="sa-badge-status sa-badge-active">Active</span>
                  </td>
                  <td style={{ fontSize: 12 }}>{fmtDate(u.created_at)}</td>
                  <td className="sa-text-right">
                    <div className="sa-row-actions">
                      <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => openAvatarPicker(u)} title="Set profile picture">
                        <Camera size={12} />
                      </button>
                      <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => setShowForm(u)} title="Edit">
                        <Pencil size={12} />
                      </button>
                      <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => handleDelete(u)} title="Delete User" style={{ color: '#dc2626' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showForm !== null && (
        <UserFormModal
          user={showForm.id ? showForm : null}
          companies={companies}
          onClose={() => setShowForm(null)}
          onSave={() => { setShowForm(null); load(); }}
        />
      )}
    </div>
  );
}
